import { useState, type ReactNode } from 'react'
import { Badge, Card, Empty, Field, Notice, PageHead, RowsSkeleton, SettingRow, Switch, Tabs } from '../components/ui.js'
import { DownloadIcon, MapPinIcon, PlusIcon, StoreIcon, TruckIcon, UploadIcon, XIcon } from '../components/icons.js'
import { money } from '../lib/format.js'
import {
  useShippingPreview,
  useShippingSettings,
  type ShippingMethodRow,
  type ShippingSettings,
  type ShippingZoneRow,
} from '../hooks/useShippingSettings.js'

/**
 * Shipping: what delivery costs, which options a shopper gets, where the store
 * delivers, when parcels leave, and what paying online is worth.
 *
 * Every field here drives the checkout's own pricing engine. The PIN code
 * tester on the right prices a cart with the *saved* settings, so it answers
 * "what does a shopper see right now", not what the unsaved form would do.
 */

type Tab = 'rates' | 'options' | 'zones' | 'dispatch' | 'prepaid'
type Set = <K extends keyof ShippingSettings>(field: K, value: ShippingSettings[K]) => void

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const METHOD_MODES: Record<ShippingMethodRow['mode'], string> = {
  rate: 'Same as shipping rules',
  add: 'Shipping rules + extra',
  fixed: 'Fixed charge',
  free: 'Free',
}

export function ShippingPage(): ReactNode {
  const { shop, settings, isPending, error, dirty, saved, saving, saveError, set, save } = useShippingSettings()
  const [tab, setTab] = useState<Tab>('rates')

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Shipping belongs to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  return (
    <>
      <PageHead
        title="Shipping"
        subtitle="Delivery charges, speeds, zones, blocked PIN codes and the prepaid discount."
        crumbs={[{ label: 'Checkout' }, { label: 'Shipping' }]}
        actions={
          <button type="button" className="btn btn--primary" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      {saved && (
        <Notice kind="ok" title="Saved">
          The checkout uses these from the next quote.
        </Notice>
      )}
      {saveError && (
        <Notice kind="error" title="Could not save">
          {(saveError as Error).message}
        </Notice>
      )}
      {dirty && !saving && (
        <Notice kind="warn" title="Unsaved changes">
          Save to apply them — the PIN code tester prices with what is saved.
        </Notice>
      )}

      {isPending ? (
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      ) : error || !settings ? (
        <Card>
          <Empty error title="Could not load your shipping settings">
            {(error as Error | null)?.message ?? 'Try again in a moment.'}
          </Empty>
        </Card>
      ) : (
        <>
          <Tabs
            tabs={[
              { key: 'rates', label: 'Charges' },
              { key: 'options', label: 'Delivery speeds' },
              { key: 'zones', label: 'Zones & PIN codes' },
              { key: 'dispatch', label: 'Dispatch & TAT' },
              { key: 'prepaid', label: 'Prepaid & COD' },
            ]}
            value={tab}
            onChange={setTab}
          />

          <div className="grid grid--main-wide">
            <div className="stack">
              {tab === 'rates' && <RatesTab s={settings} set={set} />}
              {tab === 'options' && <OptionsTab s={settings} set={set} />}
              {tab === 'zones' && <ZonesTab s={settings} set={set} />}
              {tab === 'dispatch' && <DispatchTab s={settings} set={set} />}
              {tab === 'prepaid' && <PrepaidTab s={settings} set={set} />}
            </div>

            <div className="stack">
              <PinTester />
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ── Charges ─────────────────────────────────────────────────────────────────

function RatesTab({ s, set }: { s: ShippingSettings; set: Set }): ReactNode {
  return (
    <>
      <Card title="Shipping charge" subtitle="How the base delivery charge is worked out.">
        <Field label="Charge by">
          <select
            className="input"
            value={s.shippingMode}
            onChange={(event) => set('shippingMode', event.target.value as ShippingSettings['shippingMode'])}
          >
            <option value="flat">Flat rate</option>
            <option value="slabs">Order value slabs</option>
            <option value="weight">Weight</option>
          </select>
        </Field>

        {s.shippingMode === 'flat' && (
          <Money label="Flat charge per order" value={s.shippingFlat} onChange={(v) => set('shippingFlat', v)} />
        )}

        {s.shippingMode === 'slabs' && <Slabs s={s} set={set} />}

        {s.shippingMode === 'weight' && (
          <>
            <div className="grid grid--2">
              <Num label="First (grams)" value={s.weightBaseGrams} min={1} onChange={(v) => set('weightBaseGrams', v)} />
              <Money label="costs" value={s.weightBaseRate} onChange={(v) => set('weightBaseRate', v)} />
              <Num label="Every further (grams)" value={s.weightStepGrams} min={1} onChange={(v) => set('weightStepGrams', v)} />
              <Money label="adds" value={s.weightStepRate} onChange={(v) => set('weightStepRate', v)} />
            </div>
            <Num
              label="Weight of an item with none set (grams)"
              value={s.defaultGrams}
              onChange={(v) => set('defaultGrams', v)}
            />
          </>
        )}

        <Money
          label="Extra per item"
          hint="Added on top for each item after the first. 0 for none."
          value={s.shippingPerItem}
          onChange={(v) => set('shippingPerItem', v)}
        />
      </Card>

      <Card title="Free shipping">
        <Money
          label="Free above order value"
          hint="0 turns free shipping off. The checkout shows how much more to add."
          value={s.freeShipAbove}
          onChange={(v) => set('freeShipAbove', v)}
        />
        <SettingRow title="Prepaid orders only" description="COD orders pay shipping even above the threshold.">
          <Switch label="Free shipping for prepaid only" checked={s.freeShipPrepaidOnly} onChange={(v) => set('freeShipPrepaidOnly', v)} />
        </SettingRow>
        <Money
          label="Extra shipping on COD"
          hint="Added to the shipping line for COD orders, separate from the COD fee."
          value={s.codExtra}
          onChange={(v) => set('codExtra', v)}
        />
      </Card>
    </>
  )
}

function Slabs({ s, set }: { s: ShippingSettings; set: Set }): ReactNode {
  const rows = s.shippingSlabs

  return (
    <Field label="Slabs" hint="Up to 0 means “and above”. Sorted by value when saved.">
      <div className="stack" style={{ gap: 8 }}>
        {rows.map((row, index) => (
          <div key={index} className="row" style={{ gap: 8 }}>
            <input
              type="number"
              className="input"
              min={0}
              aria-label="Up to order value"
              placeholder="Up to ₹"
              value={row.upTo}
              onChange={(e) => set('shippingSlabs', replace(rows, index, { ...row, upTo: num(e.target.value) }))}
            />
            <input
              type="number"
              className="input"
              min={0}
              aria-label="Charge"
              placeholder="Charge ₹"
              value={row.rate}
              onChange={(e) => set('shippingSlabs', replace(rows, index, { ...row, rate: num(e.target.value) }))}
            />
            <button type="button" className="icon-btn" aria-label="Remove slab" onClick={() => set('shippingSlabs', rows.filter((_, i) => i !== index))}>
              <XIcon size={16} />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => set('shippingSlabs', [...rows, { upTo: 0, rate: 0 }])}>
          <PlusIcon size={15} />
          Add slab
        </button>
      </div>
    </Field>
  )
}

// ── Delivery speeds ─────────────────────────────────────────────────────────

const PRESETS: ShippingMethodRow[] = [
  { id: 'standard', label: 'Standard delivery', hint: '', mode: 'rate', charge: 0, daysDelta: 0, enabled: true },
  { id: 'express', label: 'Express delivery', hint: 'Priority dispatch', mode: 'add', charge: 99, daysDelta: -2, enabled: true },
  { id: 'rush', label: 'Rush delivery', hint: 'Next-day in metro cities', mode: 'add', charge: 199, daysDelta: -4, enabled: true },
]

function OptionsTab({ s, set }: { s: ShippingSettings; set: Set }): ReactNode {
  const rows = s.methods
  const missing = PRESETS.filter((preset) => !rows.some((row) => row.id === preset.id))

  return (
    <Card title="Delivery speeds" subtitle="Standard, Express, Rush — the shopper picks one at checkout.">
      <SettingRow title="Offer a choice of speeds" description="Off: one standard option priced by your shipping charge.">
        <Switch label="Offer delivery speeds" checked={s.methodsEnabled} onChange={(v) => set('methodsEnabled', v)} />
      </SettingRow>

      {s.methodsEnabled && (
        <div className="stack" style={{ gap: 12 }}>
          {rows.map((row, index) => (
            <div key={row.id} className="card card--inset" style={{ padding: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                <strong>{row.label || row.id}</strong>
                <span className="row" style={{ gap: 8 }}>
                  <Switch label={`${row.label} on`} checked={row.enabled} onChange={(v) => set('methods', replace(rows, index, { ...row, enabled: v }))} />
                  {rows.length > 1 && (
                    <button type="button" className="icon-btn" aria-label={`Remove ${row.label}`} onClick={() => set('methods', rows.filter((_, i) => i !== index))}>
                      <XIcon size={16} />
                    </button>
                  )}
                </span>
              </div>
              <div className="grid grid--2">
                <Field label="Name">
                  <input className="input" value={row.label} maxLength={60} onChange={(e) => set('methods', replace(rows, index, { ...row, label: e.target.value }))} />
                </Field>
                <Field label="Note under it">
                  <input className="input" value={row.hint} maxLength={120} onChange={(e) => set('methods', replace(rows, index, { ...row, hint: e.target.value }))} />
                </Field>
                <Field label="Charge">
                  <select className="input" value={row.mode} onChange={(e) => set('methods', replace(rows, index, { ...row, mode: e.target.value as ShippingMethodRow['mode'] }))}>
                    {Object.entries(METHOD_MODES).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                {(row.mode === 'add' || row.mode === 'fixed') && (
                  <Money label={row.mode === 'add' ? 'Extra' : 'Charge'} value={row.charge} onChange={(v) => set('methods', replace(rows, index, { ...row, charge: v }))} />
                )}
                <Num
                  label="Days faster (−) or slower (+)"
                  value={row.daysDelta}
                  min={-30}
                  onChange={(v) => set('methods', replace(rows, index, { ...row, daysDelta: Math.max(-30, Math.min(30, v)) }))}
                />
              </div>
            </div>
          ))}

          {missing.length > 0 && rows.length < 6 && (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {missing.map((preset) => (
                <button key={preset.id} type="button" className="btn btn--sm btn--ghost" onClick={() => set('methods', [...rows, preset])}>
                  <PlusIcon size={15} />
                  Add {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

// ── Zones & PIN codes ───────────────────────────────────────────────────────

const BLANK_ZONE: ShippingZoneRow = { label: '', match: '', rate: null, extraDays: 0, cod: true, blocked: false }

function ZonesTab({ s, set }: { s: ShippingSettings; set: Set }): ReactNode {
  const rows = s.zones
  const [pinText, setPinText] = useState('')
  const [uploadNote, setUploadNote] = useState<string | null>(null)

  function addPins(text: string): number {
    const found = text.match(/\b[1-9]\d{5}\b/g) ?? []
    const next = [...new Set([...s.blockedPincodes, ...found])]
    set('blockedPincodes', next)
    return next.length - s.blockedPincodes.length
  }

  return (
    <>
      <Card title="Zones" subtitle="A rate, delivery time and COD rule per region. First match wins.">
        <SettingRow title="Use zones" description="Off: every PIN code gets your standard charge and delivery time.">
          <Switch label="Use zones" checked={s.zonesEnabled} onChange={(v) => set('zonesEnabled', v)} />
        </SettingRow>

        {s.zonesEnabled && (
          <div className="stack" style={{ gap: 12 }}>
            {rows.length === 0 && <p className="muted">No zones yet. Add one — e.g. “Metro” for 110, 400, 560, 600, 700.</p>}

            {rows.map((row, index) => (
              <div key={index} className="card card--inset" style={{ padding: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>{row.label || `Zone ${index + 1}`}</strong>
                  <button type="button" className="icon-btn" aria-label="Remove zone" onClick={() => set('zones', rows.filter((_, i) => i !== index))}>
                    <XIcon size={16} />
                  </button>
                </div>
                <div className="grid grid--2">
                  <Field label="Name">
                    <input className="input" value={row.label} maxLength={60} placeholder="Metro" onChange={(e) => set('zones', replace(rows, index, { ...row, label: e.target.value }))} />
                  </Field>
                  <Field label="Charge" hint="Blank: use the shipping charge rules.">
                    <input
                      type="number"
                      className="input"
                      min={0}
                      value={row.rate ?? ''}
                      onChange={(e) => set('zones', replace(rows, index, { ...row, rate: e.target.value === '' ? null : num(e.target.value) }))}
                    />
                  </Field>
                </div>
                <Field label="Covers" hint="PIN codes (110001), prefixes (110, 4*), ranges (110001-110099) or states (Maharashtra, MH), comma separated.">
                  <textarea className="input" rows={2} value={row.match} onChange={(e) => set('zones', replace(rows, index, { ...row, match: e.target.value }))} />
                </Field>
                <div className="grid grid--2">
                  <Num label="Extra delivery days (zone TAT)" value={row.extraDays} onChange={(v) => set('zones', replace(rows, index, { ...row, extraDays: Math.max(0, v) }))} />
                  <div className="stack" style={{ gap: 6, paddingTop: 22 }}>
                    <Switch label="COD available" checked={row.cod} onChange={(v) => set('zones', replace(rows, index, { ...row, cod: v }))} />
                    <Switch label="Do not deliver here" checked={row.blocked} onChange={(v) => set('zones', replace(rows, index, { ...row, blocked: v }))} />
                  </div>
                </div>
              </div>
            ))}

            <button type="button" className="btn btn--sm btn--ghost" onClick={() => set('zones', [...rows, BLANK_ZONE])}>
              <PlusIcon size={15} />
              Add zone
            </button>
          </div>
        )}
      </Card>

      <Card
        title="Non-serviceable PIN codes"
        subtitle="Shoppers with these PIN codes are told you do not deliver there, before they pay."
        action={<Badge tone="outline">{s.blockedPincodes.length.toLocaleString('en-IN')} blocked</Badge>}
      >
        <Field label="Add PIN codes" hint="Paste any list — commas, spaces or new lines.">
          <textarea className="input" rows={3} value={pinText} placeholder="110001, 110002" onChange={(e) => setPinText(e.target.value)} />
        </Field>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn--sm"
            disabled={pinText.trim() === ''}
            onClick={() => {
              const added = addPins(pinText)
              setPinText('')
              setUploadNote(`${added} new PIN code${added === 1 ? '' : 's'} added.`)
            }}
          >
            <PlusIcon size={15} />
            Add
          </button>

          <label className="btn btn--sm btn--ghost">
            <UploadIcon size={15} />
            Upload CSV
            <input
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0]
                event.target.value = ''
                if (!file) return
                void file.text().then((text) => {
                  const added = addPins(text)
                  setUploadNote(`${file.name}: ${added} new PIN code${added === 1 ? '' : 's'} added.`)
                })
              }}
            />
          </label>

          <button type="button" className="btn btn--sm btn--ghost" onClick={() => download('pincodes-sample.csv', 'pincode\n110001\n400001\n560001\n')}>
            <DownloadIcon size={15} />
            Sample CSV
          </button>

          {s.blockedPincodes.length > 0 && (
            <>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => download('blocked-pincodes.csv', `pincode\n${s.blockedPincodes.join('\n')}\n`)}>
                <DownloadIcon size={15} />
                Download list
              </button>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => confirm('Remove every blocked PIN code?') && set('blockedPincodes', [])}
              >
                Reset
              </button>
            </>
          )}
        </div>

        {uploadNote && <p className="muted" style={{ fontSize: '.85rem' }}>{uploadNote} Save to apply.</p>}

        {s.blockedPincodes.length > 0 && (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10, maxHeight: 160, overflow: 'auto' }}>
            {s.blockedPincodes.slice(0, 300).map((pin) => (
              <span key={pin} className="badge badge--outline">
                {pin}
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 18, height: 18 }}
                  aria-label={`Unblock ${pin}`}
                  onClick={() => set('blockedPincodes', s.blockedPincodes.filter((p) => p !== pin))}
                >
                  <XIcon size={12} />
                </button>
              </span>
            ))}
            {s.blockedPincodes.length > 300 && <span className="muted">+{s.blockedPincodes.length - 300} more</span>}
          </div>
        )}
      </Card>
    </>
  )
}

// ── Dispatch & TAT ──────────────────────────────────────────────────────────

function DispatchTab({ s, set }: { s: ShippingSettings; set: Set }): ReactNode {
  const [holidayText, setHolidayText] = useState(s.holidays.join(', '))

  return (
    <>
      <Card title="Pickup" subtitle="When orders leave your warehouse — this sets the delivery date the shopper sees.">
        <div className="grid grid--2">
          <Field label="Cut-off time" hint="Orders after this leave the next working day. Blank: no cut-off.">
            <input type="time" className="input" value={s.deliveryCutoff} onChange={(e) => set('deliveryCutoff', e.target.value)} />
          </Field>
          <Num label="Processing days" hint="Packing time before dispatch." value={s.processingDays} onChange={(v) => set('processingDays', Math.max(0, Math.min(30, v)))} />
        </div>

        <Field label="Dispatch days">
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {WEEKDAYS.map((day, index) => {
              const iso = index + 1
              const on = s.shipDays.includes(iso)
              return (
                <button
                  key={day}
                  type="button"
                  className={on ? 'btn btn--sm' : 'btn btn--sm btn--ghost'}
                  aria-pressed={on}
                  onClick={() => set('shipDays', on ? s.shipDays.filter((d) => d !== iso) : [...s.shipDays, iso].sort())}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </Field>

        <Field label="Holidays" hint="No dispatch on these dates, e.g. 2026-11-08, 2026-12-25.">
          <input
            className="input"
            value={holidayText}
            onChange={(e) => {
              setHolidayText(e.target.value)
              set('holidays', [...new Set(e.target.value.match(/\d{4}-\d{2}-\d{2}/g) ?? [])])
            }}
          />
        </Field>
      </Card>

      <Card title="Transit time (TAT)" subtitle="Days in transit after dispatch. Zones and delivery speeds adjust it.">
        <div className="grid grid--2">
          <Num label="Fastest" value={s.deliveryDaysMin} onChange={(v) => set('deliveryDaysMin', Math.max(0, v))} />
          <Num label="Slowest" value={s.deliveryDaysMax} onChange={(v) => set('deliveryDaysMax', Math.max(0, v))} />
        </div>

        {s.zonesEnabled && s.zones.length > 0 && (
          <div className="table-wrap" style={{ marginTop: 10 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Zone</th>
                  {(s.methodsEnabled ? s.methods.filter((m) => m.enabled) : [PRESETS[0]!]).map((method) => (
                    <th key={method.id}>{method.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.zones.filter((zone) => !zone.blocked).map((zone, index) => (
                  <tr key={index}>
                    <td>{zone.label || `Zone ${index + 1}`}</td>
                    {(s.methodsEnabled ? s.methods.filter((m) => m.enabled) : [PRESETS[0]!]).map((method) => (
                      <td key={method.id} className="num">
                        {days(s.deliveryDaysMin + zone.extraDays + method.daysDelta, s.deliveryDaysMax + zone.extraDays + method.daysDelta)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: '.8rem' }}>Transit days only — processing, cut-off and holidays are added at checkout.</p>
          </div>
        )}
      </Card>
    </>
  )
}

// ── Prepaid & COD ───────────────────────────────────────────────────────────

function PrepaidTab({ s, set }: { s: ShippingSettings; set: Set }): ReactNode {
  return (
    <>
      <Card title="Prepaid discount" subtitle="Taken off when the shopper pays online. Fewer COD returns, money upfront.">
        <SettingRow title="Give a prepaid discount">
          <Switch label="Prepaid discount" checked={s.onlineDiscountEnabled} onChange={(v) => set('onlineDiscountEnabled', v)} />
        </SettingRow>

        {s.onlineDiscountEnabled && (
          <div className="grid grid--2">
            <Field label="Type">
              <select className="input" value={s.onlineDiscountType} onChange={(e) => set('onlineDiscountType', e.target.value as 'percent' | 'fixed')}>
                <option value="percent">Percentage</option>
                <option value="fixed">Flat amount</option>
              </select>
            </Field>
            {s.onlineDiscountType === 'percent' ? (
              <Num label="Percent off" value={s.onlineDiscountValue} onChange={(v) => set('onlineDiscountValue', Math.max(0, Math.min(100, v)))} />
            ) : (
              <Money label="Amount off" value={s.onlineDiscountValue} onChange={(v) => set('onlineDiscountValue', v)} />
            )}
            {s.onlineDiscountType === 'percent' && (
              <Money label="Maximum discount" hint="0 for no cap." value={s.onlineDiscountMax} onChange={(v) => set('onlineDiscountMax', v)} />
            )}
          </div>
        )}
      </Card>

      <Card title="COD charges">
        <Money label="COD fee" hint="Added to every cash-on-delivery order. 0 for none." value={s.codFee} onChange={(v) => set('codFee', v)} />
      </Card>

      <Card title="COD to prepaid" subtitle="Win back the shopper who picks cash on delivery.">
        <SettingRow
          title="Show “Pay online and save ₹X”"
          description="When a shopper picks COD, the checkout shows what paying online would save — prepaid discount, COD fee and prepaid-only free shipping together — with a one-tap switch."
        >
          <Switch label="COD to prepaid nudge" checked={s.codPrepaidNudge} onChange={(v) => set('codPrepaidNudge', v)} />
        </SettingRow>
        {s.codPrepaidNudge && !s.onlineDiscountEnabled && s.codFee === 0 && !s.freeShipPrepaidOnly && (
          <Notice kind="warn" title="Nothing to save yet">
            Paying online costs the same as COD, so the nudge stays hidden. Add a prepaid discount or a COD fee.
          </Notice>
        )}
      </Card>
    </>
  )
}

// ── PIN code tester ─────────────────────────────────────────────────────────

function PinTester(): ReactNode {
  const preview = useShippingPreview()
  const [pincode, setPincode] = useState('')
  const [subtotal, setSubtotal] = useState(999)
  const [grams, setGrams] = useState(500)
  const [payment, setPayment] = useState<'upi' | 'cod'>('cod')

  const result = preview.data

  return (
    <Card title="Check a PIN code" subtitle="What a shopper there sees, with your saved settings.">
      <Field label="PIN code">
        <input
          className="input"
          inputMode="numeric"
          maxLength={6}
          placeholder="110001"
          value={pincode}
          onChange={(e) => setPincode(e.target.value.replace(/\D+/g, ''))}
        />
      </Field>
      <div className="grid grid--2">
        <Money label="Cart value" value={subtotal} onChange={setSubtotal} />
        <Num label="Weight (g)" value={grams} onChange={setGrams} />
      </div>
      <Field label="Payment">
        <select className="input" value={payment} onChange={(e) => setPayment(e.target.value as 'upi' | 'cod')}>
          <option value="cod">Cash on delivery</option>
          <option value="upi">Online</option>
        </select>
      </Field>

      <button
        type="button"
        className="btn btn--block"
        disabled={pincode.length !== 6 || preview.isPending}
        onClick={() => preview.mutate({ pincode, subtotal, grams, paymentMethod: payment })}
      >
        <MapPinIcon size={15} />
        {preview.isPending ? 'Checking…' : 'Check'}
      </button>

      {preview.error && <p style={{ color: 'var(--bad)', fontSize: '.85rem' }}>{preview.error.message}</p>}

      {result && (
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Badge tone={result.quote.serviceable ? 'ok' : 'bad'} dot>
              {result.quote.serviceable ? 'Delivers here' : 'Not serviceable'}
            </Badge>
            {result.quote.zone && <Badge tone="info">{result.quote.zone}</Badge>}
            <Badge tone={result.codAllowed ? 'ok' : 'warn'}>{result.codAllowed ? 'COD available' : 'No COD'}</Badge>
          </div>

          {(result.place.city || result.place.state) && (
            <p className="muted" style={{ margin: 0, fontSize: '.85rem' }}>
              {[result.place.city, result.place.state].filter(Boolean).join(', ')}
            </p>
          )}

          {result.quote.reason && <p style={{ margin: 0, color: 'var(--bad)', fontSize: '.85rem' }}>{result.quote.reason}</p>}

          {result.quote.serviceable && (
            <>
              <ul className="list" style={{ margin: 0 }}>
                {result.quote.options.map((option) => (
                  <li key={option.id}>
                    <TruckIcon size={16} />
                    <span className="list__text">
                      <strong>{option.label}</strong>
                      <small>{option.eta}</small>
                    </span>
                    <small className="nowrap">{option.charge > 0 ? money(option.charge) : 'Free'}</small>
                  </li>
                ))}
              </ul>

              <dl className="kv" style={{ margin: 0, fontSize: '.88rem' }}>
                <Line label="Shipping" value={result.totals.shipping > 0 ? money(result.totals.shipping) : 'Free'} />
                {result.totals.codFee > 0 && <Line label="COD fee" value={money(result.totals.codFee)} />}
                <Line label="Order total" value={money(result.totals.orderTotal)} strong />
                {payment === 'cod' && result.totals.prepaidTotal < result.totals.orderTotal && (
                  <Line label="If paid online" value={`${money(result.totals.prepaidTotal)} (save ${money(result.totals.orderTotal - result.totals.prepaidTotal)})`} />
                )}
              </dl>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }): ReactNode {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <dt className="muted">{label}</dt>
      <dd style={{ margin: 0, fontWeight: strong ? 650 : undefined }}>{value}</dd>
    </div>
  )
}

// ── Small inputs ────────────────────────────────────────────────────────────

function Money({ label, hint, value, onChange }: { label: string; hint?: string; value: number; onChange: (v: number) => void }): ReactNode {
  return (
    <Field label={`${label} (₹)`} hint={hint}>
      <input type="number" className="input" min={0} value={value} onChange={(e) => onChange(Math.max(0, num(e.target.value)))} />
    </Field>
  )
}

function Num({
  label,
  hint,
  value,
  min = 0,
  onChange,
}: {
  label: string
  hint?: string
  value: number
  min?: number
  onChange: (v: number) => void
}): ReactNode {
  return (
    <Field label={label} hint={hint}>
      <input type="number" className="input" min={min} step={1} value={value} onChange={(e) => onChange(Math.trunc(num(e.target.value)))} />
    </Field>
  )
}

function num(raw: string): number {
  const value = Number(raw)
  return Number.isFinite(value) ? value : 0
}

function replace<T>(rows: T[], index: number, next: T): T[] {
  return rows.map((row, i) => (i === index ? next : row))
}

function days(min: number, max: number): string {
  const lo = Math.max(0, min)
  const hi = Math.max(lo, max)
  return lo === hi ? `${lo} days` : `${lo}–${hi} days`
}

function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}
